import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useFilterContext } from "../context/FilterContext";

const categories = [
  { name: "Statuses", options: [] },
  { name: "TicketType", options: [] },
  { name: "SubStatus", options: [] },
  { name: "Source", options: [] },
];

// Labels match ops vocabulary (Day = today, Week = last 7 days). The
// underlying DateRange:<value> serialization (today/last7/last30) is
// unchanged so checkDateRange() in Layout.js keeps working as before.
const DATE_OPTIONS = ["All", "Day", "Week", "Last 30 Days", "Custom"];

function CategoryFilter({ data }) {
  const { selectedCategories, updateFilters } = useFilterContext();

  const parseDateFromCategories = () => {
    const entry = selectedCategories.find((c) => c.startsWith("DateRange:"));
    if (!entry) return { option: "All", from: "", to: "" };
    const parts = entry.substring("DateRange:".length);
    if (parts === "today") return { option: "Day", from: "", to: "" };
    if (parts === "last7") return { option: "Week", from: "", to: "" };
    if (parts === "last30") return { option: "Last 30 Days", from: "", to: "" };
    if (parts.startsWith("custom:")) {
      const [, from, to] = parts.split(":");
      return { option: "Custom", from: from || "", to: to || "" };
    }
    return { option: "All", from: "", to: "" };
  };

  const initial = parseDateFromCategories();
  const [currentDateOption, setCurrentDateOption] = useState(initial.option);
  const [customFrom, setCustomFrom] = useState(initial.from);
  const [customTo, setCustomTo] = useState(initial.to);

  useEffect(() => {
    const parsed = parseDateFromCategories();
    // Don't override Custom selection while user is picking dates
    if (currentDateOption === "Custom" && parsed.option === "All") return;
    setCurrentDateOption(parsed.option);
    setCustomFrom(parsed.from);
    setCustomTo(parsed.to);
  }, [selectedCategories]);

  const handleDateRangeChange = (option) => {
    const filtered = selectedCategories.filter((c) => !c.startsWith("DateRange:"));
    if (option === "All") {
      updateFilters(filtered);
    } else if (option === "Day") {
      updateFilters([...filtered, "DateRange:today"]);
    } else if (option === "Week") {
      updateFilters([...filtered, "DateRange:last7"]);
    } else if (option === "Last 30 Days") {
      updateFilters([...filtered, "DateRange:last30"]);
    } else if (option === "Custom") {
      setCurrentDateOption("Custom");
      if (customFrom && customTo) {
        updateFilters([...filtered, `DateRange:custom:${customFrom}:${customTo}`]);
      } else {
        updateFilters(filtered);
      }
    }
  };

  const handleCustomDateChange = (field, value) => {
    const newFrom = field === "from" ? value : customFrom;
    const newTo = field === "to" ? value : customTo;
    setCustomFrom(newFrom);
    setCustomTo(newTo);
    if (newFrom && newTo) {
      const filtered = selectedCategories.filter((c) => !c.startsWith("DateRange:"));
      updateFilters([...filtered, `DateRange:custom:${newFrom}:${newTo}`]);
    }
  };

  const handleCategoryChange = useCallback(
    (category, option) => {
      const categoryOption = `${category}:${option}`;
      const updatedCategories = selectedCategories.includes(categoryOption)
        ? selectedCategories.filter((c) => c !== categoryOption)
        : [...selectedCategories, categoryOption];

      updateFilters(updatedCategories);
    },
    [selectedCategories]
  );

  const handleSortByChange = (e) => {
    updateFilters([...selectedCategories, `Sort by:${e.target.value}`]);
  };

  const filterCategoriesHandler = useMemo(() => {
    const statusOptions = Array.from(new Set(data.map((item) => item.displayStatus)));
    const workflowtypeOptions = Array.from(
      new Set(data.map((item) => item.workflowtype))
    );

    // Extract substatus options - use metadata.subStatus if available, otherwise fall back to displayStatus
    const subStatusOptions = Array.from(
      new Set(data.map((item) => item.metadata?.subStatus || item.displayStatus).filter(Boolean))
    );

    const assigneesOptions = Array.from(
      new Set(data.map((item) => item.assignedto))
    );

    const sourceOptions = Array.from(
      new Set(
        data
          .map((item) => {
            const source = item.metadata?.source;
            if (!source) return null;
            const emailMatch = source.match(/<([^>]+)>/);
            return emailMatch ? emailMatch[1] : source;
          })
          .filter(Boolean)
      )
    );

    return categories.map((category) => {
      if (category.name === "Statuses") {
        return { ...category, options: statusOptions };
      }
      if (category.name === "Assignees") {
        return { ...category, options: assigneesOptions };
      }
      if (category.name === "TicketType") {
        return { ...category, options: workflowtypeOptions };
      }
      if (category.name === "SubStatus") {
        return { ...category, options: subStatusOptions };
      }
      if (category.name === "Source") {
        return { ...category, options: sourceOptions };
      }
      return category;
    });
  }, [data]);

  return (
    <div className="bg-background-3 h-full py-10 px-6">
      <h1 className="font-24-37-400 text-black-3">Filters</h1>
      <ul className="flex flex-col space-y-10 mt-4">
        {filterCategoriesHandler?.map((category) =>
          category.options?.length === 0 ? null : (
            <li key={category.name}>
              <h3 className="font-18-400-24 text-blue-2">{category.name}</h3>
              <ul>
                {category.options.map((option) => (
                  <li
                    key={`${category.name}:${option}`}
                    className="flex items-center gap-10"
                  >
                    <input
                      type="checkbox"
                      className="m-0 cursor-pointer"
                      id={`${category.name}:${option}`}
                      name={`${category.name}:${option}`}
                      value={`${category.name}:${option}`}
                      checked={selectedCategories.includes(
                        `${category.name}:${option}`
                      )}
                      onChange={() => handleCategoryChange(category.name, option)}
                    />
                    <label
                      className="font-18-400-24 text-blue-2 mb-0 cursor-pointer"
                      htmlFor={`${category.name}:${option}`}
                    >
                      {option}
                    </label>
                  </li>
                ))}
              </ul>
            </li>
          )
        )}
      </ul>

      <div className="mt-4">
        <h3 className="font-18-400-24 text-blue-2">Date Range</h3>
        <ul>
          {DATE_OPTIONS.map((option) => (
            <li key={option} className="flex items-center gap-10">
              <input
                type="radio"
                name="dateRange"
                id={`dateRange-${option}`}
                className="m-0 cursor-pointer"
                checked={currentDateOption === option}
                onChange={() => handleDateRangeChange(option)}
              />
              <label
                className="font-18-400-24 text-blue-2 mb-0 cursor-pointer"
                htmlFor={`dateRange-${option}`}
              >
                {option}
              </label>
            </li>
          ))}
        </ul>
        {currentDateOption === "Custom" && (
          <div className="date-filter-custom mt-2">
            <label className="font-14-400-20 text-blue-2 block mb-1">
              From:
              <input
                type="date"
                className="date-filter-input ml-2"
                value={customFrom}
                onChange={(e) => handleCustomDateChange("from", e.target.value)}
              />
            </label>
            <label className="font-14-400-20 text-blue-2 block mb-1">
              To:
              <input
                type="date"
                className="date-filter-input ml-2"
                value={customTo}
                onChange={(e) => handleCustomDateChange("to", e.target.value)}
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
}

export default CategoryFilter;
